import { fromEvent, merge, Observable } from 'rxjs';
import { filter, map, mergeMap, take, throttleTime } from 'rxjs/operators';
import { Inject, Injectable, InjectionToken } from '../../injection';
import type { StateSubjectRefresher } from '../../rxjs-utils';
import { NOTHING_TO_DO, StateSubject } from '../../rxjs-utils';

export default interface IConnectionHealthCheck {
	healthy: boolean;
	services: unknown[];
}

/**
 * Information about the Network and API connection state.
 *
 * When the network is offline, the api status will always be unknown.
 */
export interface IConnectionState {
	network: 'online' | 'offline';
	api: 'online' | 'offline' | 'unknown';
}

export const CONNECTION_STATE_REFRESHER: InjectionToken<StateSubjectRefresher<IConnectionState>> = new InjectionToken<
	StateSubjectRefresher<IConnectionState>
>('CONNECTION_STATE_REFRESHER');

@Injectable()
export class ConnectionStateService {
	public readonly STATE$: StateSubject<IConnectionState>;

	constructor(@Inject(CONNECTION_STATE_REFRESHER) connectionStateRefresher: StateSubjectRefresher<IConnectionState>) {
		this.STATE$ = new StateSubject<IConnectionState>(connectionStateRefresher);
	}

	public init(): Observable<void> {
		this.monitorNavigatorOnLineStatusChanges();
		return this.STATE$.initialize().pipe(map(() => undefined));
	}

	public setNetworkConnectionOffline(): void {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(<any>this.STATE$).next({
			api: 'unknown',
			network: 'offline',
		});
	}

	public waitForNetworkConnection(): Observable<void> {
		// NOTE: The refresh method prevents multiple API requests from being made.
		return this.STATE$.refresh().pipe(
			// Filter out all connection status events until the network is back online.
			filter((): boolean => {
				return this.STATE$.stateValue.api === 'online';
			}),
			take(1),
			map(() => undefined)
		);
	}

	/**
	 * Monitors changes to navigator.onLine flag and performs an API call once online, to make sure
	 * that the API connection is working properly.
	 */
	private monitorNavigatorOnLineStatusChanges(): void {
		merge(
			fromEvent(window, 'online').pipe(
				throttleTime<Event>(250),
				mergeMap((): Observable<void> => {
					return this.STATE$.refresh();
				})
			),
			fromEvent(window, 'offline').pipe(
				throttleTime<Event>(250),
				map((): void => {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(<any>this.STATE$).next({ api: 'unknown', network: 'offline' });
				})
			)
		).subscribe(NOTHING_TO_DO);
	}
}
